import { provide } from "vue";
import { ApiError, err } from "@/api/base";
import FileDTO = App.Data.FileDTO;

/**
 * Mutation handlers are only consumed through `response.isOk()` guards at the
 * call sites that need them, so the contract here stays deliberately loose:
 * consumers hand over whatever their API client returns (or nothing at all).
 */
type HandlerResult = Promise<unknown> | unknown;

export type FileBrowserActionHandlers = {
	openFile?: (file: FileDTO) => HandlerResult;
	updateFileProperties?: (file: FileDTO) => HandlerResult;
	createFile?: (file: FileDTO) => HandlerResult;
	removeFile?: (file: FileDTO) => HandlerResult;
	batchRemoveFiles?: (files: FileDTO[]) => HandlerResult;
	closeAction?: () => HandlerResult;
	updateDefinition?: (file: FileDTO) => HandlerResult;
	toggleIsFavorite?: (file: FileDTO) => HandlerResult;
};

/**
 * Stands in for a handler the parent did not supply. Returning an `Err` keeps
 * the `response.isOk()` contract the browser components rely on, so an
 * unreachable-by-configuration action degrades into a no-op instead of a
 * `response.isOk is not a function` crash.
 */
const unhandled = (name: keyof FileBrowserActionHandlers) => {
	return () => {
		console.error(
			`FileBrowser: "${name}" was triggered but no handler was passed to useFileFinder().`
		);

		return Promise.resolve(
			err<never, ApiError>(
				new ApiError(501, `No "${name}" handler was provided.`)
			)
		);
	};
};

/**
 * Publishes the parent's data/mutation handlers to the file browser tree.
 *
 * Handlers are keyed rather than positional: every consumer only needs the ones
 * its `actions` list can actually reach.
 */
export const useFileFinder = (handlers: FileBrowserActionHandlers): void => {
	provide("openFileAction", handlers.openFile ?? unhandled("openFile"));
	provide(
		"updateFilePropertiesAction",
		handlers.updateFileProperties ?? unhandled("updateFileProperties")
	);
	provide("createFileAction", handlers.createFile ?? unhandled("createFile"));
	provide("removeFileAction", handlers.removeFile ?? unhandled("removeFile"));
	provide(
		"batchRemoveFilesAction",
		handlers.batchRemoveFiles ?? unhandled("batchRemoveFiles")
	);
	provide("closeAction", handlers.closeAction ?? unhandled("closeAction"));
	provide(
		"updateDefinitionAction",
		handlers.updateDefinition ?? unhandled("updateDefinition")
	);
	provide(
		"toggleIsFavoriteAction",
		handlers.toggleIsFavorite ?? unhandled("toggleIsFavorite")
	);
};
